/* PDP09 Variant B presentation-only runtime. */
(function () {
  'use strict';

  var ROOT_SELECTOR = 'section.cln-pdp09.fable-variant-b';
  var DETAILS_SELECTOR = [
    'details.included',
    'details.fable-program',
    'details.cert-details',
    'details.fable-faq-item'
  ].join(', ');
  var DETAILS_GROUP_SELECTOR = [
    '[data-variant-b-details-group]',
    '.fable-program-grid',
    '.fable-faq-library',
    '.cert-panel',
    '.buybox'
  ].join(', ');
  var initializedRoots = typeof WeakSet === 'function' ? new WeakSet() : null;

  function one(root, selector) {
    return root.querySelector(selector);
  }

  function all(root, selector) {
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  function reducedMotion() {
    return Boolean(
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function scrollBehavior() {
    return reducedMotion() ? 'auto' : 'smooth';
  }

  function setDisabled(button, disabled) {
    if (!button) return;
    button.disabled = disabled;
    button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  }

  function detailsGroup(item) {
    return item.closest(DETAILS_GROUP_SELECTOR);
  }

  function detailsInGroup(group) {
    return all(group, DETAILS_SELECTOR).filter(function (item) {
      return detailsGroup(item) === group;
    });
  }

  function syncDetailsGroup(group) {
    if (!group || !group.classList.contains('fable-program-grid')) return;
    var hasOpenProgram = detailsInGroup(group).some(function (item) {
      return item.classList.contains('fable-program') && item.open;
    });
    group.classList.toggle('has-open', hasOpenProgram);
  }

  function initDetails(root) {
    var items = all(root, DETAILS_SELECTOR);
    var groups = [];

    items.forEach(function (item) {
      item.removeAttribute('open');
      var group = detailsGroup(item);
      if (group && groups.indexOf(group) === -1) groups.push(group);

      item.addEventListener('toggle', function () {
        var ownGroup = detailsGroup(item);
        if (!ownGroup) return;

        if (item.open) {
          detailsInGroup(ownGroup).forEach(function (other) {
            if (other !== item && other.open) other.open = false;
          });
        }
        syncDetailsGroup(ownGroup);
      });
    });

    groups.forEach(syncDetailsGroup);
  }

  function initProgramModule(module) {
    var track = one(module, '.fable-program-grid');
    var nav = one(module, '.fable-program-nav');
    var previous = one(module, '[data-program-prev]');
    var next = one(module, '[data-program-next]');
    if (!track || !nav || !previous || !next) return;

    var frame = 0;

    function programCards() {
      return all(track, 'details.fable-program');
    }

    function gap() {
      var styles = window.getComputedStyle(track);
      return Number.parseFloat(styles.columnGap || styles.gap) || 12;
    }

    function step() {
      var card = programCards()[0];
      return (card ? card.getBoundingClientRect().width : track.clientWidth) + gap();
    }

    function refresh() {
      frame = 0;
      var desktop = window.matchMedia && window.matchMedia('(min-width: 1200px)').matches;
      var maximum = Math.max(0, track.scrollWidth - track.clientWidth);
      var position = Math.max(0, Math.min(maximum, track.scrollLeft));
      var overflow = !desktop && maximum > 1;

      nav.hidden = !overflow;
      setDisabled(previous, !overflow);
      setDisabled(next, !overflow);
    }

    function scheduleRefresh() {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(refresh);
    }

    function move(direction) {
      var maximum = Math.max(0, track.scrollWidth - track.clientWidth);
      if (maximum <= 1) return;
      var position = Math.max(0, Math.min(maximum, track.scrollLeft));
      if (direction < 0 && position <= 2) {
        track.scrollTo({ left: maximum, behavior: 'auto' });
      } else if (direction > 0 && position >= maximum - 2) {
        track.scrollTo({ left: 0, behavior: 'auto' });
      } else {
        track.scrollBy({
          left: direction * step(),
          behavior: scrollBehavior()
        });
      }
      scheduleRefresh();
    }

    previous.addEventListener('click', function () { move(-1); });
    next.addEventListener('click', function () { move(1); });
    track.addEventListener('scroll', scheduleRefresh, { passive: true });
    window.addEventListener('resize', scheduleRefresh, { passive: true });

    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(scheduleRefresh).observe(track);
    }

    refresh();
  }

  function initSelectionSummary(root) {
    var summaries = all(root, '[data-vb-selection-summary]');
    if (!summaries.length) return;

    function selectedChoice(selector) {
      var choices = all(root, selector);
      for (var i = 0; i < choices.length; i++) {
        var input = one(choices[i], 'input[type="radio"]');
        if (input && input.checked) return choices[i];
      }
      return one(root, selector + '[data-selected="true"]');
    }

    function selectedColor(selector, fallback) {
      var selected = selectedChoice(selector);
      var label = selected && one(selected, '[data-cln-color-name]');
      return label ? label.textContent.trim() : fallback;
    }

    function refresh() {
      var duo = selectedChoice('[data-dc-tpl="37"]');
      var first = selectedColor('[data-cln-dev1]', 'Classic White');
      var second = selectedColor('[data-cln-dev2]', first);
      var price = root.getAttribute(duo ? 'data-cln-price-2x' : 'data-cln-price-1x') || '';
      // Keep the locale-formatted price from the Liquid data attribute.
      var text = duo
        ? (first === second
          ? '2× ' + first + ' · ' + price
          : '1× ' + first + ' + 1× ' + second + ' · ' + price)
        : '1× ' + first + ' · ' + price;
      summaries.forEach(function (summary) { summary.textContent = text; });
    }

    if ('MutationObserver' in window) {
      new MutationObserver(function (records) {
        if (records.some(function (record) {
          return record.attributeName === 'data-selected';
        })) refresh();
      }).observe(root, {
        subtree: true,
        attributes: true,
        attributeFilter: ['data-selected']
      });
    }
    root.addEventListener('cln:pdp09-selection-change', refresh);

    refresh();
  }

  function initHeader(root) {
    var header = one(root, '.site-header');
    var nav = one(root, '[data-primary-nav]');
    var button = one(root, '[data-menu-button]');
    if (!header || !nav || !button) return;

    function desktop() {
      return Boolean(window.matchMedia && window.matchMedia('(min-width: 768px)').matches);
    }

    function closeMobileMenu() {
      nav.classList.remove('is-open');
      button.setAttribute('aria-expanded', 'false');
      nav.setAttribute('aria-hidden', desktop() ? 'false' : 'true');
    }

    function syncViewport() {
      if (desktop()) {
        nav.classList.remove('is-open');
        nav.removeAttribute('hidden');
        nav.setAttribute('aria-hidden', 'false');
        button.setAttribute('aria-expanded', 'false');
      } else if (!nav.classList.contains('is-open')) {
        nav.setAttribute('hidden', '');
        nav.setAttribute('aria-hidden', 'true');
      }
    }

    button.addEventListener('click', function () {
      var opening = !nav.classList.contains('is-open');
      nav.classList.toggle('is-open', opening);
      button.setAttribute('aria-expanded', opening ? 'true' : 'false');
      nav.setAttribute('aria-hidden', opening ? 'false' : 'true');
      if (opening) nav.removeAttribute('hidden');
      else nav.setAttribute('hidden', '');
    });

    all(nav, 'a').forEach(function (link) {
      link.addEventListener('click', function () {
        if (!desktop()) {
          closeMobileMenu();
          nav.setAttribute('hidden', '');
        }
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && nav.classList.contains('is-open')) {
        closeMobileMenu();
        nav.setAttribute('hidden', '');
        button.focus();
      }
    });

    window.addEventListener('resize', syncViewport, { passive: true });
    syncViewport();
  }

  function initRoot(root) {
    if (!root) return;
    if (initializedRoots) {
      if (initializedRoots.has(root)) return;
      initializedRoots.add(root);
    } else if (root.__clnPdp09VariantBInitialized) {
      return;
    } else {
      root.__clnPdp09VariantBInitialized = true;
    }

    initDetails(root);
    initHeader(root);
    all(root, '.fable-programs').forEach(initProgramModule);
    initSelectionSummary(root);
  }

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll(ROOT_SELECTOR), initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var scope = event && event.target;
    if (!scope) return;
    if (scope.matches && scope.matches(ROOT_SELECTOR)) initRoot(scope);
    if (scope.querySelectorAll) {
      Array.prototype.forEach.call(scope.querySelectorAll(ROOT_SELECTOR), initRoot);
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var scope = event && event.target;
    if (scope && scope.querySelector && scope.querySelector(ROOT_SELECTOR)) {
      document.documentElement.classList.remove('cln-pdp09-variant-b-route');
    }
  });
})();

/* VB stage-context chip: bind labels to stable gallery media identities, never to positions. */
(function () {
  var root = document.querySelector('section.cln-pdp09.fable-variant-b');
  if (!root) return;
  var chip = root.querySelector('[data-stage-context]');
  var counter = root.querySelector('[data-dc-tpl="27"] .sc-interp');
  var thumbs = root.querySelector('[data-cln-thumbs]');
  if (!chip || !counter || !thumbs) return;
  var whiteIndex = parseInt(thumbs.getAttribute('data-cln-white-idx'), 10);
  var greyIndex = parseInt(thumbs.getAttribute('data-cln-grey-idx'), 10);
  function sync() {
    var m = /^(\d+)\s*\//.exec((counter.textContent || '').trim());
    var activeIndex = m ? parseInt(m[1], 10) - 1 : -1;
    var label = activeIndex === whiteIndex ? 'Classic White' : activeIndex === greyIndex ? 'Slate Grey' : '';
    chip.textContent = label;
    chip.hidden = !label;
  }
  new MutationObserver(sync).observe(counter, { childList: true, characterData: true, subtree: true });
  sync();
})();

/* VB sticky-stepper: jump naar buybox + qty/prijs-sync met offerkeuze (proto-gedrag) */
(function () {
  var root = document.querySelector('section.cln-pdp09.fable-variant-b');
  if (!root) return;
  var jump = root.querySelector('[data-buybox-jump]');
  if (jump) jump.addEventListener('click', function () {
    var bb = root.querySelector('#buybox');
    if (!bb) return;
    var jy = Math.max(0, Math.round(bb.getBoundingClientRect().top + window.scrollY) - 12);
    if (typeof window.__clnNavScroll === 'function') window.__clnNavScroll(jy);
    else bb.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
  /* Zichtbaarheid: eigen toggle (legacy-JS toggelt de nieuwe markup niet) - tonen zodra de buybox-CTA uit beeld is en de footer nog niet in beeld, alleen <1024px */
  var stick = root.querySelector('.sticky-cart');
  var primaryCta = root.querySelector('#buybox [data-cln-buy-cta]') || root.querySelector('[data-cln-buy-cta]');
  var siteFoot = root.querySelector('.site-footer');
  var visTick = false;
  function setStick(show) {
    if (!stick || stick.hidden === !show) return;
    stick.hidden = !show;
    stick.setAttribute('aria-hidden', show ? 'false' : 'true');
  }
  function updateStick() {
    visTick = false;
    if (!stick) return;
    var show = window.scrollY > 24;
    if (show && primaryCta) {
      var r = primaryCta.getBoundingClientRect();
      show = r.bottom < 0 || r.top > window.innerHeight;
    }
    if (show && siteFoot) {
      var f = siteFoot.getBoundingClientRect();
      if (f.top < window.innerHeight && f.bottom > 0) show = false;
    }
    setStick(show);
  }
  function onScrollStick() { if (!visTick) { visTick = true; requestAnimationFrame(updateStick); } }
  if (stick) {
    window.addEventListener('scroll', onScrollStick, { passive: true });
    window.addEventListener('resize', onScrollStick);
    // D3: layout-mutaties zonder scroll (offer-wissel toont/verbergt kleurrij) herpositioneren de CTA;
    // IO op de hero-CTA + na-offer-klik-recheck houden de hide-state synchroon zodat de balk nooit de CTA bedekt.
    if ('IntersectionObserver' in window && primaryCta) {
      try { new IntersectionObserver(function () { onScrollStick(); }, { threshold: [0, 0.5, 1] }).observe(primaryCta); } catch (eio) {}
    }
    root.addEventListener('click', function (ev) {
      if (ev.target && ev.target.closest && ev.target.closest('.offer-option')) setTimeout(updateStick, 150);
    });
    updateStick();
  }
  var qtyEl = stick ? stick.querySelector('[data-current-qty]') : null;
  var priceEl = stick ? stick.querySelector('[data-current-price]') : null;
  var p1 = root.getAttribute('data-cln-price-1x') || '';
  var p2 = root.getAttribute('data-cln-price-2x') || '';
  function syncFromOffer() {
    var two = false;
    var options = root.querySelectorAll('.offer-option');
    var sel = null;
    for (var i = 0; i < options.length; i++) {
      var input = options[i].querySelector('input[type="radio"]');
      if (input && input.checked) { sel = options[i]; break; }
    }
    if (!sel) sel = root.querySelector('.offer-option[data-selected="true"]');
    if (sel) { two = /2/.test((sel.textContent || '').slice(0, 4)); }
    if (qtyEl) qtyEl.textContent = two ? '2\u00d7' : '1\u00d7';
    if (priceEl && (two ? p2 : p1)) priceEl.textContent = two ? p2 : p1;
  }
  root.addEventListener('click', function (e) {
    if (e.target && e.target.closest && e.target.closest('.offer-option')) setTimeout(syncFromOffer, 60);
  });
  root.addEventListener('cln:pdp09-selection-change', syncFromOffer);
})();


/* ===== EB-MEDIA-INLINE-LIGHTBOX (2026-07-13, Fable) =====================================
   (1) ECHTE ROUTINES-video's: klik op de video speelt INLINE af (niet meer default fullscreen);
       los fullscreen-icoon per frame voor optionele native fullscreen.
   (2) Acht levens-foto's: klik opent een fullscreen swipe-carousel (bestaande persona-lightbox
       bedraad + inline overlay-styling; naar <body> verplaatst voor gegarandeerde viewport-fix).
   Volledig additief: raakt cln-pdp09.js NIET. Onderschept de bestaande openLb-binding in de
   capture-fase, zodat een parallelle Codex-sessie op cln-pdp09.js ongemoeid blijft.
   ====================================================================================== */
(function () {
  var root = document.querySelector('section.cln-pdp09.fable-variant-b');
  if (!root || root.__clnMediaInline) return;
  root.__clnMediaInline = true;

  /* ---------- (1) UGC-video's: klik = inline play; los fullscreen-icoon ---------- */
  function ensureLoaded(v) {
    if (!v || v.__clnInlineLoaded) return;
    var src = v.getAttribute('data-cln-video-src');
    if (src && !v.querySelector('source')) {
      var s = document.createElement('source');
      s.src = src;
      var t = v.getAttribute('data-cln-video-type');
      if (t) s.type = t;
      v.appendChild(s);
      try { v.load(); } catch (e) {}
    }
    v.__clnInlineLoaded = true;
  }
  function pauseOtherMedia(except) {
    root.querySelectorAll('[data-cln-ugc-card] video').forEach(function (o) {
      if (o !== except) { try { o.pause(); } catch (e) {} }
    });
    root.querySelectorAll('.cln-gallery-video').forEach(function (g) {
      try { g.pause(); } catch (e) {}
      g.muted = true;
    });
  }
  /* Capture-fase: onderschep de play-knop VOOR de openLb-binding van cln-pdp09.js.
     Klik = native controls tonen (afspeelbalk: pauze/geluid/timeline/fullscreen) net als de gallery-video's,
     overlay verbergen zodat de native controls bruikbaar zijn, en inline afspelen met geluid. */
  root.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-cln-ugc-play]') : null;
    if (!btn || !root.contains(btn)) return;
    e.stopPropagation();
    e.preventDefault();
    var card = btn.closest('[data-cln-ugc-card]'); if (!card) return;
    var v = card.querySelector('video'); if (!v) return;
    ensureLoaded(v);
    v.setAttribute('controls', '');
    v.setAttribute('controlslist', 'nodownload');
    v.setAttribute('playsinline', '');
    try { v.muted = false; } catch (e3) {}
    btn.style.display = 'none';
    if (!v.__clnUgcEnded) {
      v.__clnUgcEnded = true;
      v.addEventListener('ended', function () { btn.style.display = ''; });
    }
    pauseOtherMedia(v);
    var p = v.play(); if (p && p.catch) p.catch(function () {});
  }, true);

  /* ---------- (2) Acht levens-foto's -> fullscreen swipe-carousel ---------- */
  var opens = Array.prototype.slice.call(root.querySelectorAll('[data-cln-persona-open]'));
  var lb = root.querySelector('[data-cln-persona-lb]');
  if (opens.length && lb) {
    if (lb.parentElement !== document.body) document.body.appendChild(lb); // uit transformed ancestor -> echt viewport-fixed
    var stage = lb.querySelector('[data-cln-persona-stage]');
    var closeBtn = lb.querySelector('[data-cln-persona-close]');
    var arrows = Array.prototype.filter.call(lb.children, function (n) { return n.tagName === 'BUTTON'; });
    var prevBtn = arrows[0] || null, nextBtn = arrows[1] || null;
    var items = opens.map(function (b) {
      var im = b.querySelector('img');
      return {
        src: (b.getAttribute('data-cln-persona-full') || '').replace(/&amp;/g, '&'),
        alt: (im && im.getAttribute('alt')) || ''
      };
    });
    var idx = 0, lastFocus = null;

    lb.style.cssText = 'position:fixed;inset:0;z-index:99999;display:none;align-items:center;justify-content:center;background:rgba(20,18,16,.92);padding:24px;';
    var inner = lb.querySelector('.p09-plb-inner');
    if (inner) inner.style.cssText = 'position:relative;max-width:min(1100px,94vw);max-height:88vh;display:flex;align-items:center;justify-content:center;';
    if (stage) stage.style.cssText = 'display:flex;align-items:center;justify-content:center;max-width:100%;max-height:88vh;';
    if (closeBtn) closeBtn.style.cssText = 'position:absolute;top:-8px;right:-8px;z-index:6;width:44px;height:44px;border:0;border-radius:999px;background:rgba(251,247,240,.95);color:#3a2e25;font:400 24px/1 sans-serif;cursor:pointer;display:flex;align-items:center;justify-content:center;';

    function render() {
      if (!stage) return;
      var it = items[idx];
      stage.innerHTML = '';
      var img = document.createElement('img');
      img.src = it.src;
      img.alt = it.alt || '';
      img.decoding = 'async';
      img.style.cssText = 'max-width:100%;max-height:88vh;width:auto;height:auto;object-fit:contain;border-radius:12px;display:block;';
      stage.appendChild(img);
    }
    function show(i) { idx = (i + items.length) % items.length; render(); }
    function onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); show(idx - 1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); show(idx + 1); }
    }
    function open(i) {
      lastFocus = document.activeElement;
      show(i);
      lb.removeAttribute('hidden');
      lb.style.display = 'flex';
      document.documentElement.style.overflow = 'hidden';
      document.addEventListener('keydown', onKey, true);
      if (closeBtn) closeBtn.focus();
    }
    function close() {
      lb.setAttribute('hidden', 'hidden');
      lb.style.display = 'none';
      document.documentElement.style.overflow = '';
      document.removeEventListener('keydown', onKey, true);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    root.addEventListener('click', function (e) {
      var ob = e.target && e.target.closest ? e.target.closest('[data-cln-persona-open]') : null;
      if (!ob || !root.contains(ob)) return;
      e.preventDefault();
      e.stopPropagation();
      var i = opens.indexOf(ob); if (i < 0) i = 0;
      open(i);
    });
    if (prevBtn) prevBtn.addEventListener('click', function (e) { e.stopPropagation(); show(idx - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function (e) { e.stopPropagation(); show(idx + 1); });
    if (closeBtn) closeBtn.addEventListener('click', function (e) { e.stopPropagation(); close(); });
    lb.addEventListener('click', function (e) { if (e.target === lb) close(); });

    var x0 = null;
    lb.addEventListener('touchstart', function (e) { x0 = e.touches[0].clientX; }, { passive: true });
    lb.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) > 40) show(dx < 0 ? idx + 1 : idx - 1);
      x0 = null;
    }, { passive: true });
  }
})();


/* ===== EB-TP-003 (2026-07-13, Fable): mobiel eyebrow links + Trustpilot rechts, één regel boven de gallery.
   Kloon de Trustpilot naast de mobile-context eyebrow (boven de gallery). Origineel blijft in de buybox
   voor desktop; op mobiel wordt de buybox-kicker-row verborgen via CSS. Additief; raakt Codex-JS niet. ==== */
(function () {
  var root = document.querySelector('section.cln-pdp09.fable-variant-b');
  if (!root || root.__clnMcTpRow) return;
  var mc = root.querySelector('.mobile-context');
  var tp = root.querySelector('.buybox-trustpilot');
  if (!mc || !tp) return;
  if (root.querySelector('[data-cln-mc-row]')) { root.__clnMcTpRow = true; return; }
  root.__clnMcTpRow = true;
  var row = document.createElement('div');
  row.setAttribute('data-cln-mc-row', '');
  mc.parentNode.insertBefore(row, mc);
  row.appendChild(mc);                 // eyebrow links
  var clone = tp.cloneNode(true);      // Trustpilot rechts (statische kloon: sterren + score-link)
  clone.setAttribute('data-cln-tp-mobileclone', '');
  clone.removeAttribute('id');
  row.appendChild(clone);
})();

/* ===== EB-SYNC-001 (2026-07-13, Fable): twee-richtings offer-sync buybox <-> offer-kaarten onderin =====
   - Default: enkel (1x) geselecteerd, boven EN onder.
   - Keuze in de buybox -> kaarten onderin volgen (bruine rand via .featured).
   - Klik op kaart onderin -> buybox volgt (via echte click op de offer-rij zodat de bestaande
     setOffer-keten draait: prijzen/kleurrijen/sticky) ; klik op de kaart-CTA -> ook terug naar de buybox.
   - CTA-klik wordt in capture-fase onderschept: blokkeert de oude directe-ATC-binding en de anchor-jump. */
(function () {
  var root = document.querySelector('section.cln-pdp09.fable-variant-b');
  if (!root || root.__clnOfferSync) return;
  root.__clnOfferSync = true;
  var cards = Array.prototype.slice.call(root.querySelectorAll('.offer-decision-card'));
  if (!cards.length) return;
  function cardOffer(card) {
    var a = card.querySelector('[data-offer-jump]');
    return (a && a.getAttribute('data-offer-jump') === '2') ? 2 : 1;
  }
  function optOffer(opt) {
    return /2/.test((opt.textContent || '').trim().slice(0, 3)) ? 2 : 1;
  }
  function currentOffer() {
    var opts = root.querySelectorAll('.offer-option');
    var sel = null;
    for (var i = 0; i < opts.length; i++) {
      var input = opts[i].querySelector('input[type="radio"]');
      if (input && input.checked) { sel = opts[i]; break; }
    }
    if (!sel) sel = root.querySelector('.offer-option[data-selected="true"]');
    return sel ? optOffer(sel) : 1;
  }
  function findOpt(want) {
    var opts = root.querySelectorAll('.offer-option');
    for (var i = 0; i < opts.length; i++) { if (optOffer(opts[i]) === want) return opts[i]; }
    return null;
  }
  function paintCards(o) {
    cards.forEach(function (c) {
      var mine = cardOffer(c) === o;
      c.classList.toggle('featured', mine);
      c.setAttribute('aria-checked', mine ? 'true' : 'false');
    });
  }
  function selectTop(want) {
    if (currentOffer() !== want) {
      var t = findOpt(want);
      if (t) {
        /* RC-2: de radio-.click() geeft de off-screen radio native focus -> UA scrollt hem in beeld.
           Herstel de scrollpositie synchroon en blur, zodat er geen focus-flits ontstaat; de definitieve
           scroll komt daarna uit window.__clnNavScroll. */
        var _y = window.scrollY;
        t.click();
        window.scrollTo(0, _y);
        if (document.activeElement && document.activeElement.blur) { try { document.activeElement.blur(); } catch (e0) {} }
      }
    }
    paintCards(want);
  }
  /* boven -> onder */
  root.addEventListener('click', function (e) {
    if (e.target && e.target.closest && e.target.closest('.offer-option')) {
      setTimeout(function () { paintCards(currentOffer()); }, 80);
    }
  });
  root.addEventListener('cln:pdp09-selection-change', function () {
    paintCards(currentOffer());
  });
  /* onder: CTA-klik = keuze overnemen + terug naar buybox (capture blokkeert oude bindingen + anchor) */
  var jumpLock = false;
  /* document-CAPTURE + stopImmediatePropagation: vuurt VOOR de Webflow-anker-handler (bubble) en blokkeert die volledig. */
  document.addEventListener('click', function (e) {
    var act = e.target && e.target.closest ? e.target.closest('.offer-decision-card .offer-card-action') : null;
    if (!act || !root.contains(act)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (jumpLock) return;
    jumpLock = true;
    try { act.blur(); } catch (e2) {}
    try { selectTop(act.getAttribute('data-offer-jump') === '2' ? 2 : 1); } catch (e3) {}
    setTimeout(function () {
      try {
        var bb = root.querySelector('#buybox');
        if (bb) {
          var jy = Math.max(0, Math.round(bb.getBoundingClientRect().top + window.scrollY) - 12);
          if (typeof window.__clnNavScroll === 'function') window.__clnNavScroll(jy);
          else window.scrollTo(0, jy);
        }
      } catch (e4) {}
      setTimeout(function () { jumpLock = false; }, 900); // reset ALTIJD (D7)
    }, 260);
  }, true);
  /* onder: klik op de kaart zelf = keuze overnemen (zonder scroll) */
  cards.forEach(function (c) {
    c.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('.offer-card-action')) return;
      selectTop(cardOffer(c));
    });
  });
  /* init: spiegel de huidige buybox-staat (default = enkel) */
  paintCards(currentOffer());
})();


/* ===== EB-FNAV-JS CC029 (2026-07-13, Fable): footer Explore/FAQ ankerlinks -> deterministische 650ms-scroll met header-offset.
   Native smooth over 15-20k px is te traag; globale scroll-behavior:smooth gaf bijwerkingen op andere scrolls. */
(function () {
  var root = document.querySelector('section.cln-pdp09.fable-variant-b');
  if (!root || root.__clnFootNav) return;
  root.__clnFootNav = true;
  var foot = root.querySelector('footer.site-footer');
  if (!foot) return;
  function headerOffset() {
    var h = document.getElementById('shopify-section-sections--28345557287256__header');
    var hh = h && h.offsetHeight ? h.offsetHeight : (window.innerWidth < 768 ? 56 : 68);
    return hh + 16;
  }
  var navAnimId = 0;
  function cancelNavAnim() { navAnimId++; }
  window.addEventListener('wheel', cancelNavAnim, { passive: true });      // D9: gebruiker onderbreekt = stoppen
  window.addEventListener('touchstart', cancelNavAnim, { passive: true });
  function animateTo(y, recheck) {
    var my = ++navAnimId;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      window.scrollTo(0, typeof recheck === 'function' ? (recheck() !== null ? recheck() : y) : y); return;
    }
    var y0 = window.scrollY, dy = y - y0, dur = 650, t0 = null;
    function step(ts) {
      if (my !== navAnimId) return; // geannuleerd (nieuwe nav of user-input)
      if (t0 === null) t0 = ts;
      var p = Math.min(1, (ts - t0) / dur);
      var e = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
      window.scrollTo(0, y0 + dy * e);
      if (p < 1) { requestAnimationFrame(step); return; }
      // eind-hercorrectie: lazy-load kan het doel tijdens de animatie verschuiven -> 1 korte tweede pass
      if (typeof recheck === 'function') {
        var y2 = recheck();
        if (y2 !== null && Math.abs(y2 - (y0 + dy)) > 24) {
          var c0 = window.scrollY, cd = y2 - c0, ct0 = null;
          function step2(ts2) {
            if (my !== navAnimId) return;
            if (ct0 === null) ct0 = ts2;
            var q = Math.min(1, (ts2 - ct0) / 220);
            window.scrollTo(0, c0 + cd * q);
            if (q < 1) requestAnimationFrame(step2);
          }
          requestAnimationFrame(step2);
        }
      }
    }
    requestAnimationFrame(step);
  }
  /* Gedeelde deterministische scroll (single source of truth); Webflow-concurrent wordt geblokkeerd. */
  window.__clnNavScroll = function (y) { animateTo(y); };
  /* document-CAPTURE + stopImmediatePropagation: onderschep footer-ankerlinks VOOR Webflow (bubble). */
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('a[href^="#"]') : null;
    if (!a || !foot.contains(a)) return;
    var id = a.getAttribute('href').slice(1);
    var el = document.getElementById(id);
    if (!el) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    try { a.blur(); } catch (e2) {}
    var y = Math.max(0, Math.round(el.getBoundingClientRect().top + window.scrollY) - headerOffset());
    if (id === 'top-buybox') y = 0;
    animateTo(y, function () {
      var el2 = document.getElementById(id);
      if (!el2) return null;
      if (id === 'top-buybox') return 0;
      return Math.max(0, Math.round(el2.getBoundingClientRect().top + window.scrollY) - headerOffset());
    });
  }, true);
})();


/* ===== EB-3D-13 (2026-07-14, Fable): dedicated 3D-slide (model-viewer). Lib (Google CDN 4.0.0, zelfde als slot-10-aanpak)
   en GLB laden PAS bij activatie van de slide (mobile-performance). Kliks binnen de 3D-slide bereiken de
   gallery-lightbox nooit (capture-stop) zodat muis-rotatie vrij werkt. Swatches wisselen White/Grey GLB. */
(function () {
  var root = document.querySelector('section.cln-pdp09.fable-variant-b');
  if (!root || root.__cln3d) return;
  root.__cln3d = true;
  var mv = root.querySelector('[data-cln-3d]');
  if (!mv) return;
  var slide = mv.closest('[data-cln-slide]');
  var inited = false;
  function libLoad() {
    if (document.getElementById('cln-mv-lib')) return;
    var s = document.createElement('script');
    s.id = 'cln-mv-lib'; s.type = 'module';
    s.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/4.0.0/model-viewer.min.js';
    document.head.appendChild(s);
  }
  function init() {
    if (inited) return;
    inited = true;
    libLoad();
    mv.setAttribute('src', mv.getAttribute('data-src-white'));
  }
  function maybe() {
    if (slide && getComputedStyle(slide).opacity !== '0') init();
  }
  root.addEventListener('click', function (e) {
    if (e.target && e.target.closest && (e.target.closest('[data-cln-thumb]') || e.target.closest('.gallery-arrow'))) setTimeout(maybe, 90);
  });
  if (slide) slide.addEventListener('click', function (e) {
    /* swatches moeten hun eigen (bubble-)handler bereiken; alle overige kliks in de 3D-slide
       blijven geblokkeerd zodat de gallery-lightbox nooit opent tijdens rotatie */
    if (e.target && e.target.closest && e.target.closest('[data-cln-3d-swatches]')) return;
    e.stopPropagation();
  }, true);
  root.querySelectorAll('[data-cln-3d-color]').forEach(function (b) {
    b.addEventListener('click', function (ev) {
      ev.stopPropagation();
      init();
      var c = b.getAttribute('data-cln-3d-color');
      mv.setAttribute('src', mv.getAttribute(c === 'grey' ? 'data-src-grey' : 'data-src-white'));
      root.querySelectorAll('[data-cln-3d-color]').forEach(function (x) {
        var on = x === b;
        x.setAttribute('aria-pressed', on ? 'true' : 'false');
        x.style.borderColor = on ? '#9E5438' : 'rgba(32,35,34,.25)';
      });
    });
  });
})();
